import { useFormikContext } from 'formik';
import React, { useContext, useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import useDownloadTripLogTemplate from '../../../../hooks/dashboard/trip-log/useDownloadTripLogTemplate';
import useUploadTripLogListExcel from '../../../../hooks/dashboard/trip-log/useUploadTripLogListExcel';
import {
  SetUpADriverAccountFromExcelInitialValues,
  SetUpADriverAccountFromExcelSchema,
} from '../../../../models/dashboard/driver-setup/setup-drivers-account-from-excel';
import NotificationBanner from '../../../Banner/NotificationBanner';
import CancelCsvUploadFormSubmitButton from '../../../Button/CancelCsvUploadFormSubmitButton';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import ResetFormSubmitButton from '../../../Button/ResetFormSubmitButton';
import { FormComponent } from '../../../FormCom/FormComponent';
import UploadExcelFileField from '../../../FormCom/UploadExcelFileField/UploadExcelFileField';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';

interface UploadTripLogExcelFileModalProps {}

const UploadTripLogExcelFileModal: React.FC<
  UploadTripLogExcelFileModalProps
> = () => {
  const modalContext = useContext(ModalContext);
  const [fileUploaded, setFileUploaded] = useState(false);
  const { uploadTripLogListExcel, loading } = useUploadTripLogListExcel();
  const { downloadTripLogTemplate, loading: loadingDownload } =
    useDownloadTripLogTemplate();
  const {
    downloadTripLogTemplate: downloadTripLogTemplateV2,
    loading: loadingDownloadV2,
  } = useDownloadTripLogTemplate();

  const onSubmit = async (value: any, actions: any) => {
    uploadTripLogListExcel(value, () => actions.resetForm());
  };

  const onCancel = () => {
    modalContext?.setShowModal(false);
  };

  const onDownloadTemplate = () => {
    downloadTripLogTemplate();
  };

  return (
    <ModalContainer
      width={!modalContext?.showSuccessComponent ? '867px' : undefined}
      label='Upload Excel File'
      description={
        fileUploaded
          ? 'Please review the data before confirming:'
          : 'Upload your Excel file with trip details.'
      }
    >
      <FormComponent
        initialValues={SetUpADriverAccountFromExcelInitialValues}
        schema={SetUpADriverAccountFromExcelSchema}
        onSubmit={onSubmit}
        className={'small-gap'}
      >
        <FileUploadObserver onFileChange={setFileUploaded} />
        <FileUploadBanner />
        <CsvViewerContainer>
          <UploadExcelFileField
            label='Driver Credentials File'
            name='excelFile'
            id='excelFile'
            placeholder='Drag and drop your CSV or Excel file here'
          />
        </CsvViewerContainer>

        <DownloadTemplate onClick={onDownloadTemplate}>
          Download Template V1{loadingDownload && <LoadingOutlined />}
        </DownloadTemplate>

        <DownloadTemplate onClick={() => downloadTripLogTemplateV2(2)}>
          Download Template V2{loadingDownloadV2 && <LoadingOutlined />}
        </DownloadTemplate>

        <CancelCsvUploadFormSubmitButton
          className='clear large cancel'
          onClick={onCancel}
        >
          Cancel
        </CancelCsvUploadFormSubmitButton>

        <FormButtonArea>
          <ResetFormSubmitButton className='clear large' detachMode={true}>
            Cancel
          </ResetFormSubmitButton>
          <FormSubmitButton
            className='blue large'
            loading={loading}
            detachMode={true}
          >
            Confirm
          </FormSubmitButton>
        </FormButtonArea>
      </FormComponent>
    </ModalContainer>
  );
};

export default UploadTripLogExcelFileModal;

const FileUploadObserver: React.FC<{
  onFileChange: (hasFile: boolean) => void;
}> = ({ onFileChange }) => {
  const { values } = useFormikContext<{ excelFile: File | null }>();
  useEffect(() => {
    onFileChange(!!values.excelFile);
  }, [values.excelFile, onFileChange]);
  return null;
};

const FileUploadBanner: React.FC = () => {
  const { values } = useFormikContext<{ excelFile: File | null }>();
  if (!values.excelFile) return null;
  return (
    <Wrapper>
      <NotificationBanner message="Ensure the driver's name and customer's email are accurate. Please check carefully before confirming. This cannot be changed once confirmed." />
    </Wrapper>
  );
};

const FormButtonArea = styled.div`
  margin-top: 32px;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`;

const DownloadTemplate = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-top: 0px;
  color: #1f2130;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CsvViewerContainer = styled.div`
  padding: 16px;
  background-color: #fafafa;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;

  overflow-x: auto;
`;

const Wrapper = styled.div`
  margin-bottom: 26px;
`;
